import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card, Row, Col, Statistic, Table, Tag, Typography, Button, Space, Divider, Tooltip, Alert,
} from 'antd';
import {
  ArrowLeftOutlined, SendOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ClockCircleOutlined, StopOutlined,
  EyeOutlined, GroupOutlined, BarChartOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchPushHistory,
  selectPushMessages,
  selectPushLoading,
} from 'store/slices/push.slice';
import { PushMessage, PushStatus, PushMessageGroup } from 'types/push.types';
import { formatters } from 'utils/formatters';
import { ROUTES } from 'config/routes';

const { Title, Text } = Typography;

const PushGroupDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { group, appId, segmentId, filters: parentFilters } =
    (location.state as {
      group: PushMessageGroup;
      appId?: number;
      segmentId?: number;
      filters?: any;
    }) || {};

  const messages = useAppSelector(selectPushMessages);
  const isLoading = useAppSelector(selectPushLoading);

  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    if (!group) return;
    const filter: any = {
      search: group.title,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    };
    if (appId) filter.app_id = appId;
    if (segmentId) filter.segment_id = segmentId;
    if (parentFilters?.date_from) filter.date_from = parentFilters.date_from;
    if (parentFilters?.date_to) filter.date_to = parentFilters.date_to;
    if (parentFilters?.push_type) filter.push_type = parentFilters.push_type;
    if (parentFilters?.status) filter.status = parentFilters.status;

    dispatch(fetchPushHistory(filter));
  }, [group, page, appId, segmentId]);

  if (!group) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Назад
        </Button>
        <Alert
          type="warning"
          message="Данные группы не найдены"
          description="Вернитесь к списку пушей и выберите группу заново."
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  const deliveryRate = group.total_sent > 0
    ? (group.total_delivered / group.total_sent) * 100
    : 0;
  const ctr = group.total_delivered > 0
    ? (group.total_clicked / group.total_delivered) * 100
    : 0;
  const filteredMessages = messages.filter(
    m => m.title === group.title && m.body === group.body
  );

  const getStatusTag = (status: PushStatus) => {
    const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      [PushStatus.SCHEDULED]: { color: 'blue', icon: <ClockCircleOutlined />, label: 'Запланировано' },
      [PushStatus.SENDING]: { color: 'processing', icon: <SendOutlined />, label: 'Отправляется' },
      [PushStatus.SENT]: { color: 'success', icon: <CheckCircleOutlined />, label: 'Отправлено' },
      [PushStatus.FAILED]: { color: 'error', icon: <CloseCircleOutlined />, label: 'Ошибка' },
      [PushStatus.CANCELLED]: { color: 'default', icon: <StopOutlined />, label: 'Отменено' },
    };
    const c = config[status];
    return <Tag icon={c?.icon} color={c?.color}>{c?.label || status}</Tag>;
  };

  const columns: ColumnsType<PushMessage> = [
    {
      title: 'Message ID',
      dataIndex: 'message_id',
      key: 'message_id',
      width: 200,
      render: (id: string) => (
        <Text code style={{ fontSize: 11 }}>{id.slice(0, 16)}...</Text>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: PushStatus) => getStatusTag(status),
    },
    {
      title: 'Отправлено',
      dataIndex: 'sent_count',
      key: 'sent_count',
      width: 100,
      align: 'right',
      render: (v: number) => formatters.number(v),
    },
    {
      title: 'Доставлено',
      dataIndex: 'delivered_count',
      key: 'delivered_count',
      width: 100,
      align: 'right',
      render: (v: number) => formatters.number(v),
    },
    {
      title: 'Кликов',
      dataIndex: 'clicked_count',
      key: 'clicked_count',
      width: 100,
      align: 'right',
      render: (v: number) => formatters.number(v),
    },
    {
      title: 'Время отправки',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 160,
      render: (date: string) => date ? formatters.dateTime(date) : '-',
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 60,
      render: (_, record) => (
        <Tooltip title="Детализация">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(ROUTES.PUSH_DETAIL.replace(':messageId', record.message_id))}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Назад
        </Button>
        <Divider type="vertical" />
        <GroupOutlined style={{ fontSize: 20, color: 'var(--accent)' }} />
        <Title level={4} style={{ margin: 0 }}>
          Группа пушей ({group.count} шт.)
        </Title>
      </Space>
      <Card size="small" style={{ marginBottom: 24 }}>
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
          {group.title}
        </Text>
        <Text type="secondary">{group.body}</Text>
      </Card>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Всего отправлено"
              value={group.total_sent}
              prefix={<SendOutlined style={{ color: 'var(--accent)' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Доставлено"
              value={group.total_delivered}
              suffix={<Text type="secondary">({deliveryRate.toFixed(1)}%)</Text>}
              prefix={<CheckCircleOutlined style={{ color: 'var(--success)' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Кликов"
              value={group.total_clicked}
              suffix={<Text type="secondary">({ctr.toFixed(1)}%)</Text>}
              prefix={<ThunderboltOutlined style={{ color: 'var(--accent-purple)' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Ошибки"
              value={group.total_failed}
              prefix={<CloseCircleOutlined style={{ color: 'var(--danger)' }} />}
            />
          </Card>
        </Col>
      </Row>
      <Card
        title={<Space><BarChartOutlined /><span>Отдельные отправки</span></Space>}
        size="small"
      >
        <Table
          columns={columns}
          dataSource={filteredMessages}
          rowKey="message_id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: group.count,
            onChange: setPage,
            showTotal: (t, r) => `${r[0]}-${r[1]} из ${t}`,
          }}
          scroll={{ x: 900 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default PushGroupDetail;